/** Pure Change log logic: the wording of each row, the filters, and the "edited after" count. */
export interface ChangeRow {
  kind: 'value' | 'setup';
  changed_at: string;
  who: string | null;
  via_link: boolean;
  link_person: string | null;
  section: string;
  metric_id: string;
  metric: string;
  entry_date: string | null;
  old_value: number | null;
  new_value: number | null;
  change: 'renamed' | 'target' | 'shown' | 'hidden' | 'redefined' | null;
  old_text: string | null;
  new_text: string | null;
}
export interface ChangeLine {
  when: string;
  who: string;
  metric: string;
  day: string;
  label: string;
  from: string;
  to: string;
  how: string;
}
export interface LogFilters {
  section: string;
  who: string;
  period: 'all' | '7d' | '30d' | 'month';
  editsOnly: boolean;
  metric: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BLANK = 'blank';

function manilaParts(iso: string | Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(typeof iso === 'string' ? new Date(iso) : iso);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    time: `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`,
  };
}
export function manilaDay(iso: string | Date): string {
  const p = manilaParts(iso);
  return `${String(p.year)}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
const shortDay = (day: string) =>
  `${String(Number(day.slice(8, 10)))} ${MONTHS[Number(day.slice(5, 7)) - 1] ?? ''}`;
const number = (value: number | null) =>
  value === null
    ? BLANK
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
const text = (value: string | null) => (value === null || value === '' ? BLANK : value);

/** The person shown for a row. A link save names who the link is for, never who made the link. */
function person(row: ChangeRow): string {
  if (!row.via_link) return row.who ?? 'Unknown';
  const name = row.link_person?.trim();
  return name ? `${name} (ads entry link)` : 'Ads entry link';
}
const SETUP_LABELS = {
  renamed: 'Renamed',
  target: 'Target changed',
  hidden: 'Hidden',
  shown: 'Shown',
  redefined: 'Formula or type changed',
} as const;

export function changeLine(row: ChangeRow): ChangeLine {
  const p = manilaParts(row.changed_at);
  const when = `${String(p.day)} ${MONTHS[p.month - 1] ?? ''}, ${p.time}`;
  const base = { when, who: person(row), metric: `${row.section} · ${row.metric}` };
  if (row.kind === 'setup') {
    const change = row.change ?? 'renamed';
    const showsValues = change === 'renamed' || change === 'target';
    return {
      ...base,
      day: '',
      label: SETUP_LABELS[change],
      from: showsValues ? text(row.old_text) : '',
      to: showsValues ? text(row.new_text) : '',
      how: row.via_link ? 'Ads entry link' : 'Setup',
    };
  }
  return {
    ...base,
    day: row.entry_date ? shortDay(row.entry_date) : '',
    label: row.old_value === null ? 'First entry' : row.new_value === null ? 'Cleared' : 'Changed',
    from: number(row.old_value),
    to: number(row.new_value),
    how: row.via_link ? 'Ads entry link' : 'Hub',
  };
}

function periodStart(period: LogFilters['period'], now: Date): string | null {
  if (period === 'all') return null;
  const today = manilaDay(now);
  if (period === 'month') return `${today.slice(0, 7)}-01`;
  const back = period === '7d' ? 6 : 29;
  return new Date(Date.parse(`${today}T00:00:00Z`) - back * 86_400_000).toISOString().slice(0, 10);
}
export function filterLog(rows: ChangeRow[], filters: LogFilters, now = new Date()): ChangeRow[] {
  const from = periodStart(filters.period, now);
  return rows.filter((row) => {
    if (filters.section && row.section !== filters.section) return false;
    if (filters.metric && row.metric_id !== filters.metric) return false;
    if (filters.who && person(row) !== filters.who) return false;
    if (from && manilaDay(row.changed_at) < from) return false;
    if (filters.editsOnly && row.kind === 'value' && row.old_value === null) return false;
    return true;
  });
}
/** Number saves made after a report finished. A report still running has nothing after it. */
export function editsAfter(rows: ChangeRow[], finishedAt: string | null): number {
  if (!finishedAt) return 0;
  const cutoff = Date.parse(finishedAt);
  return rows.filter((r) => r.kind === 'value' && Date.parse(r.changed_at) > cutoff).length;
}
